import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import {
  CONVERSATION_PERMISSIONS,
  ROLES,
} from 'dashboard/constants/permissions';
import { useAlert } from 'dashboard/composables';

// Gate the dashboard outlet before mounting a feature page or fetching its data.
// The same policy drives sidebar visibility and preserves edition restrictions.
export function useRouteFeatureAccess() {
  const route = useRoute();
  const router = useRouter();
  const { t } = useI18n();
  const { currentAccount, accountScopedRoute } = useAccount();
  const { shouldShow } = usePolicy();
  const accountLoaded = computed(() => !!currentAccount.value?.id);
  const canAccessFeature = computed(() => {
    const { featureFlag, permissions, installationTypes } = route.meta;
    if (!featureFlag) return true;
    return (
      accountLoaded.value &&
      shouldShow(featureFlag, permissions, installationTypes)
    );
  });

  watch(
    [() => route.fullPath, accountLoaded, canAccessFeature],
    () => {
      if (
        !route.meta.featureFlag ||
        !accountLoaded.value ||
        canAccessFeature.value
      )
        return;
      useAlert(t('GENERAL.PAGE_NOT_FOUND'));
      const destination = shouldShow(null, [
        ...ROLES,
        ...CONVERSATION_PERMISSIONS,
      ])
        ? 'home'
        : 'account_no_access';
      router.replace(accountScopedRoute(destination));
    },
    { immediate: true }
  );

  return { canAccessFeature };
}
